import * as React from 'react';
import { observer } from 'mobx-react';
import { IBasicTableColumn, IBasicTableProps, CheckboxField } from 'ui-lib';
import { DevicePackagesStore, IDisplayDevicePackage } from '../../../../../store/device-details/device-packages-store';
import { IDeviceTableProps } from './components';
import { DeviceReportTable } from './device-report-table';
import { PACKAGE_TYPE } from 'dto/packages-dto';
import { getRangeDateTimeFilterColumnProps } from '../../../components/table-utils';
import { useDeviceStore, usePackagesStore } from 'store';

const INSTALLED_PACKAGE_STATUS = {
    INSTALLED: true,
    DOWNLOAD_ONLY: false,
};
export const DevicePackagesTableComponent = observer((props: IDeviceTableProps) => {
    const packagesStore = usePackagesStore();
    const devicesStore = useDeviceStore();
    const memoizedStore = React.useMemo(() => {
        const store = new DevicePackagesStore({
            accountId: props.accountId,
            uid: props.uid,
            devicesStore: devicesStore,
            serialNumber: props.serialNumber,
        }, packagesStore);
        store.initialize();
        return store;
    }, []);

    const columns: IBasicTableColumn[] = [
        {
            code: 'packageName',
            title: 'Package',
            dataIndex: 'packageName',
            width: '35%',
            sorter: (a: IDisplayDevicePackage, b: IDisplayDevicePackage) => a.packageName?.localeCompare(b.packageName),
            textSearchPlaceholder: 'Search Package Name',
            onFilter: (packageName: string, pkg: IDisplayDevicePackage) =>
                pkg.packageName?.toLowerCase().includes(packageName.toLowerCase()),
            iconName: 'search',
        },
        {
            code: 'version',
            title: 'Version',
            dataIndex: 'version',
            width: '20%',
            sorter: (a: IDisplayDevicePackage, b: IDisplayDevicePackage) => a.version?.localeCompare(b.version),
            textSearchPlaceholder: 'Search Version',
            onFilter: (version: string, pkg: IDisplayDevicePackage) =>
                pkg.version?.toLowerCase().includes(version.toLowerCase()),
            iconName: 'search',
        },
        {
            code: 'type',
            title: 'Type',
            dataIndex: 'type',
            width: '10%',
            sorter: (a: IDisplayDevicePackage, b: IDisplayDevicePackage) => a.type?.localeCompare(b.type),
            defaultSortOrder: 'ascend',
            filters: Object.keys(PACKAGE_TYPE).map((type) => ({ text: PACKAGE_TYPE[type], value: PACKAGE_TYPE[type] })),
            onFilter: (type: string, item: IDisplayDevicePackage) => item.type == type,
        },
        {
            code: 'arch',
            title: 'Architecture',
            dataIndex: 'arch',
            width: '10%',
            filters: memoizedStore.archFilterTypes,
            onFilter: (type: string, item: IDisplayDevicePackage) => item.arch == type,
            sorter: (a: IDisplayDevicePackage, b: IDisplayDevicePackage) => a.arch?.localeCompare(b.arch),
        },
        {
            code: 'installedAtTimestamp',
            title: 'Last Updated',
            dataIndex: 'installedAtTimestamp',
            width: '20%',
            defaultSortOrder: 'descend',
            ...getRangeDateTimeFilterColumnProps('installedAtTimestamp'),
            sorter: (a: IDisplayDevicePackage, b: IDisplayDevicePackage) =>
                a.installedAtTimestamp?.localeCompare(b.installedAtTimestamp),
        },
        {
            code: 'installed',
            title: 'Installed',
            dataIndex: 'installed',
            width: '5%',
            filters: Object.keys(INSTALLED_PACKAGE_STATUS).map(status => ({ text: status, value: INSTALLED_PACKAGE_STATUS[status] })),
            onFilter: (status: boolean, devicePackage: IDisplayDevicePackage) => devicePackage.installed === status,
            render: (value: boolean, record: IDisplayDevicePackage) => <CheckboxField label='' checked={value}/>
        },
    ];

    const table: IBasicTableProps = { columns, dataSource: memoizedStore.displayPackages };

    return <DeviceReportTable store={memoizedStore} table={table} />;
});
